'use client';

import { useId } from 'react';

import type { OrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { CARD, OrderThumb } from '@eliadmin/core/src/components/nt/account/orders/order-parts';
import {
  countLabels,
  useOrderTexts,
} from '@eliadmin/core/src/components/nt/account/orders/order-texts';
import { orderCounts, usePrice } from '@eliadmin/core/src/components/nt/account/orders/orders-data';
import Link from '@eliadmin/core/src/components/link';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { fillTemplate } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type OrderLine = CustomerOrder['items'][number];

/** The ordered variant's values („Čierna · XL“), else its name; empty for a plain product. */
export function variantLine(line: OrderLine | undefined) {
  const values = (line?.variant?.values ?? []).map(value => value.name).filter(Boolean);
  return values.length ? values.join(' · ') : (line?.variant?.name ?? '');
}

type TotalRow = { key: string; label: string; value: string; positive?: boolean };

/**
 * Figma „Card · Produkty“ (208:12024): 56 px photo, name (linked to the product) and variant,
 * „quantity × unit price“ and the line total – on phones the prices go on a row under the name –
 * then the subtotal, discount, delivery and payment rows and „Spolu s DPH“. The API has neither
 * the promotion savings nor the coupon code of an order: „Ušetrené na akciách“ is not shown and a
 * discount shows as the core „Zľava“ – the lines with delivery and payment minus the order total.
 * `order` – the customer's own order, whose list carries the product links and variants of the
 * lines; without it (a guest has no such list, and `/orders/info` has neither slug nor variant) the
 * lines come from `info` alone and are shown as plain names.
 */
export function OrderItemsCard({ order, info }: { order?: CustomerOrder; info: OrderInfo }) {
  const { t } = useTranslations();
  const { checkout } = useOrderTexts();
  const price = usePrice();
  const titleId = useId();
  const quantity = new Intl.NumberFormat('sk');
  const lines = new Map((order?.items ?? []).map(item => [item.id, item]));
  const items = info.items ?? [];

  const subtotal = items.reduce((sum, item) => sum + item.price_with_vat * item.quantity, 0);
  const shipping = info.shipment_price_with_vat ?? 0;
  const payment = info.payment_type_price_with_vat ?? 0;
  // Per-unit rounding of the lines can leave a cent: only a real difference is a discount.
  const difference = subtotal + shipping + payment - info.price_with_vat;
  const discount = difference > 0.015 ? difference : 0;
  const vat = info.price_with_vat - (info.price_without_vat ?? info.price);
  const counts = countLabels(checkout, orderCounts(items));
  const charge = (value: number): Pick<TotalRow, 'value' | 'positive'> =>
    value ? { value: price(value) } : { value: t('text1152'), positive: true };

  const rows: TotalRow[] = [
    {
      key: 'subtotal',
      label: checkout && counts ? fillTemplate(checkout.subtotal, counts) : t('text675'),
      value: price(subtotal),
    },
  ];
  if (discount >= 0.01) {
    rows.push({
      key: 'discount',
      label: t('text24'),
      value: `−${price(discount)}`,
      positive: true,
    });
  }
  if (info.shipment) {
    rows.push({
      key: 'shipping',
      label: `${t('text153')} · ${info.shipment.name}`,
      ...charge(shipping),
    });
  }
  if (info.payment_type) {
    rows.push({ key: 'payment', label: info.payment_type.name, ...charge(payment) });
  }

  return (
    <section aria-labelledby={titleId} className={cn(CARD, 'flex flex-col gap-4 p-4 md:p-6')}>
      <h2 id={titleId} className="nt-h5 text-[#0D110D]">
        {t('text150')}
      </h2>
      <ul className="flex flex-col">
        {items.map(item => {
          const line = lines.get(item.id);
          const slug = line?.product?.slug;
          const variant = variantLine(line);
          return (
            <li
              key={item.id}
              className="flex items-start gap-4 border-b border-[#E2E5DF] py-3.5 last:border-b-0 @[40rem]:items-center"
            >
              <OrderThumb image={item.image} />
              <div className="flex min-w-0 flex-1 flex-col gap-2 @[40rem]:flex-row @[40rem]:items-center @[40rem]:gap-4">
                <div className="flex min-w-0 flex-col gap-0.5 @[40rem]:flex-1">
                  <p className="nt-body-l-bold text-[#0D110D]">
                    {slug ? (
                      <Link
                        href={`/${slug}`}
                        prefetch={false}
                        className="rounded transition-colors hover:text-[#0E6A20] focus-visible:shadow-focus focus-visible:outline-none"
                      >
                        {item.name}
                      </Link>
                    ) : (
                      item.name
                    )}
                  </p>
                  {variant ? <p className="nt-caption text-[#5D655D]">{variant}</p> : null}
                </div>
                <div className="flex items-center justify-between gap-4 whitespace-nowrap @[40rem]:contents">
                  <p className="nt-body-m text-[#5D655D]">
                    {quantity.format(item.quantity)} × {price(item.price_with_vat)}
                  </p>
                  <p className="nt-price-m text-right text-[#0D110D] @[40rem]:w-[100px]">
                    {price(item.price_with_vat * item.quantity)}
                  </p>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <dl className="flex flex-col gap-2 border-t border-[#E2E5DF] pt-4">
        {rows.map(row => (
          <div key={row.key} className="flex items-start justify-between gap-2">
            <dt className="nt-body-m min-w-0 text-[#363D36]">{row.label}</dt>
            <dd
              className={cn(
                'nt-body-m-bold whitespace-nowrap',
                row.positive ? 'text-[#0E6A20]' : 'text-[#0D110D]',
              )}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 flex-col">
          {checkout?.totalWithVat ? (
            <p className="nt-body-l-bold text-[#0D110D]">{checkout.totalWithVat}</p>
          ) : null}
          {checkout?.vatIncluded && vat > 0 ? (
            <p className="nt-caption text-[#5D655D]">
              {fillTemplate(checkout.vatIncluded, { suma: price(vat) })}
            </p>
          ) : null}
        </div>
        <p className="nt-price-l whitespace-nowrap text-[#0D110D]">{price(info.price_with_vat)}</p>
      </div>
    </section>
  );
}
